import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { cmsAPI, settingsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Menu, Phone, MapPin, Clock, Truck, User, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { SalesChatbot } from '@/components/SalesChatbot';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Inventory', path: '/inventory' },
  { label: 'Financing', path: '/financing' },
  { label: 'About', path: '/about' },
];

export default function PublicLayout() {
  const { user, logout, isStaff } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerCfg, setHeaderCfg] = useState({});
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [footerLogoUrl, setFooterLogoUrl] = useState(DEFAULT_LOGO);
  const [footerCfg, setFooterCfg] = useState({
    description: "Canada's trusted source for quality commercial trucks and trailers. Serving the transportation industry with integrity since 2005.",
    address: "5939 Shawson Dr Suite #100\nMississauga, ON L4W 3Y2",
    phone: "(416) 417-2525",
    toll_free: "(844) 417-2525",
    email: "info@canadiantrucksales.ca",
    hours_weekday: "Mon - Fri: 9:00 AM - 6:00 PM",
    hours_saturday: "Sat: 10:00 AM - 5:00 PM",
    hours_sunday: "Sun: Closed",
  });

  useEffect(() => {
    cmsAPI.get('header').then(r => setHeaderCfg(r.data || {})).catch(() => {});
    cmsAPI.get('footer').then(r => {
      if (r.data) setFooterCfg(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
    settingsAPI.getBranding().then(r => {
      if (r.data?.primary_logo) setLogoUrl(resolveImageUrl(r.data.primary_logo));
      if (r.data?.secondary_logo) {
        setFooterLogoUrl(resolveImageUrl(r.data.secondary_logo));
      } else if (r.data?.primary_logo) {
        setFooterLogoUrl(resolveImageUrl(r.data.primary_logo));
      }
    }).catch(() => {});
  }, []);

  const logoStyle = {
    height: headerCfg.logo_height ? `${headerCfg.logo_height}px` : '48px',
    width: headerCfg.logo_width ? `${headerCfg.logo_width}px` : 'auto',
  };
  const footerLogoStyle = {
    height: headerCfg.footer_logo_height ? `${headerCfg.footer_logo_height}px` : '40px',
    width: headerCfg.footer_logo_width ? `${headerCfg.footer_logo_width}px` : 'auto',
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="public-layout">
      {/* Utility Bar */}
      <div className="bg-[#0A0A0A] text-gray-300 text-xs hidden md:block">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 flex justify-between items-center h-10">
          <div className="flex items-center gap-6">
            <a href="tel:+14164172525" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone className="w-3 h-3" /> (416) 417-2525
            </a>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> Mississauga, ON
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Mon-Fri 9AM-6PM
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                {isStaff && <Link to="/admin" className="hover:text-white transition-colors flex items-center gap-1.5"><User className="w-3 h-3" /> Admin Portal</Link>}
                {!isStaff && <Link to="/portal" className="hover:text-white transition-colors flex items-center gap-1.5"><User className="w-3 h-3" /> My Account</Link>}
                <button onClick={logout} className="hover:text-white transition-colors">Logout</button>
              </>
            ) : (
              <Link to="/login" className="hover:text-white transition-colors flex items-center gap-1.5">
                <User className="w-3 h-3" /> Client Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm" data-testid="main-header">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={logoUrl} alt="Canadian Truck Sales" style={logoStyle} data-testid="header-logo" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium uppercase tracking-wide transition-colors hover:text-[#E31937] ${
                  location.pathname === link.path ? 'text-[#E31937]' : 'text-[#0A0A0A]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              className="border-2 border-gray-200 text-[#0A0A0A] hover:border-[#0A0A0A] hover:bg-gray-50 rounded-md font-medium uppercase tracking-wide text-xs h-10 px-5"
              onClick={() => navigate('/financing')}
              data-testid="nav-get-financing"
            >
              Get Financing
            </Button>
            <Button
              className="bg-[#E31937] hover:bg-[#C4122C] text-white rounded-md font-medium uppercase tracking-wide text-xs h-10 px-5 shadow-lg shadow-red-900/20"
              onClick={() => navigate('/inventory')}
              data-testid="nav-view-inventory"
            >
              <Truck className="w-4 h-4 mr-1.5" /> View Inventory
            </Button>
          </div>

          {/* Mobile: Phone + Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <a href="tel:+14164172525" className="p-2 text-[#0A0A0A]">
              <Phone className="w-5 h-5" />
            </a>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" className="text-[#0A0A0A]">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="p-6 border-b border-gray-100">
                  <img src={logoUrl} alt="CTS" className="h-10 w-auto" />
                </div>
                <nav className="flex flex-col p-6 gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`text-base font-medium uppercase tracking-wide py-3 border-b border-gray-50 transition-colors ${
                        location.pathname === link.path ? 'text-[#E31937]' : 'text-[#0A0A0A]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  {user ? (
                    <div className="flex flex-col gap-1">
                      {isStaff && <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-base font-medium uppercase tracking-wide py-3 border-b border-gray-50 text-[#E31937]">Admin Portal</Link>}
                      {!isStaff && <Link to="/portal" onClick={() => setMobileOpen(false)} className="text-base font-medium uppercase tracking-wide py-3 border-b border-gray-50 text-[#E31937]">My Account</Link>}
                      <button onClick={() => { logout(); setMobileOpen(false); }} className="text-base font-medium uppercase tracking-wide py-3 border-b border-gray-50 text-gray-500 text-left">Logout</button>
                    </div>
                  ) : (
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="text-base font-medium uppercase tracking-wide py-3 border-b border-gray-50 text-[#E31937]">
                      Client Login
                    </Link>
                  )}
                  <div className="flex flex-col gap-3 mt-6">
                    <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white w-full h-12 font-medium uppercase tracking-wide rounded-md" onClick={() => { navigate('/inventory'); setMobileOpen(false); }}>
                      <Truck className="w-4 h-4 mr-2" /> View Inventory
                    </Button>
                    <Button variant="outline" className="border-2 border-gray-200 text-[#0A0A0A] w-full h-12 font-medium uppercase tracking-wide rounded-md" onClick={() => { navigate('/financing'); setMobileOpen(false); }}>
                      Get Financing
                    </Button>
                  </div>
                  <div className="mt-8 space-y-3 text-sm text-gray-500">
                    <a href="tel:+14164172525" className="flex items-center gap-2 hover:text-[#E31937]"><Phone className="w-4 h-4" /> (416) 417-2525</a>
                    <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Mississauga, ON</span>
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Mon-Fri 9AM-6PM</span>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* WhatsApp Chat Widget */}
      <SalesChatbot />

      {/* Footer */}
      <footer className="bg-[#0A0A0A]" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand column */}
            <div>
              <div className="mb-5">
                <img src={footerLogoUrl} alt="Canadian Truck Sales" style={footerLogoStyle} data-testid="footer-logo" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                {footerCfg.description}
              </p>
            </div>

            {/* Inventory column */}
            <div>
              <h4 className="text-white font-semibold uppercase tracking-wider mb-6 text-sm">
                Inventory
              </h4>
              <div className="flex flex-col gap-3">
                <Link to="/inventory?type=truck" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-trucks">Heavy Duty Trucks</Link>
                <Link to="/inventory?type=trailer" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-trailers">Trailers</Link>
                <Link to="/inventory?featured=true" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-featured">Featured Units</Link>
                <Link to="/inventory" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-all">All Inventory</Link>
              </div>
            </div>

            {/* Quick Links column */}
            <div>
              <h4 className="text-white font-semibold uppercase tracking-wider mb-6 text-sm">
                Quick Links
              </h4>
              <div className="flex flex-col gap-3">
                <Link to="/financing" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-financing">Financing Application</Link>
                <Link to="/about" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-about">About Us</Link>
                <Link to="/login" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="footer-link-portal">Client Portal</Link>
              </div>
            </div>

            {/* Contact column */}
            <div>
              <h4 className="text-white font-semibold uppercase tracking-wider mb-6 text-sm">
                Contact
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E31937]" />
                  <span className="whitespace-pre-line">{footerCfg.address}</span>
                </div>
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E31937]" />
                  <div>
                    <a href={`tel:${footerCfg.phone?.replace(/[^0-9+]/g, '')}`} className="hover:text-white transition-colors block">{footerCfg.phone}</a>
                    {footerCfg.toll_free && <a href={`tel:${footerCfg.toll_free?.replace(/[^0-9+]/g, '')}`} className="hover:text-white transition-colors block">Toll-free: {footerCfg.toll_free}</a>}
                  </div>
                </div>
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E31937]" />
                  <a href={`mailto:${footerCfg.email}`} className="hover:text-white transition-colors">{footerCfg.email}</a>
                </div>
                <div className="flex items-start gap-3 text-gray-400 text-sm">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E31937]" />
                  <div>
                    {footerCfg.hours_weekday && <span className="block">{footerCfg.hours_weekday}</span>}
                    {footerCfg.hours_saturday && <span className="block">{footerCfg.hours_saturday}</span>}
                    {footerCfg.hours_sunday && <span className="block">{footerCfg.hours_sunday}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-5 flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-zinc-500 text-xs">
              &copy; {new Date().getFullYear()} Canadian Truck Sales. All rights reserved.
            </p>
            <div className="flex gap-6 text-zinc-500 text-xs">
              <span className="hover:text-white cursor-pointer transition-colors">Privacy Policy</span>
              <span className="hover:text-white cursor-pointer transition-colors">Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
